/**
 * Converts a string from camelCase to snake_case.
 * @param {string} str - The string in camelCase format to be converted.
 * @returns {string} - The string converted to snake_case format.
 */
export const camelToSnakeCase = (str: string): string =>
	str?.replace(/[A-Z]/g, (letter) => `_${letter.toLowerCase()}`);

/**
 * Converts a string from snake_case to camelCase.
 * @param {string} str - The string in snake_case format to be converted.
 * @returns {string} - The string converted to camelCase format.
 */
export const snakeToCamelCase = (str: string): string =>
	str?.replace(/_([a-z])/g, (match, letter) => letter.toUpperCase());

/**
 * Checks if a given string is a valid URL.
 * @param {string} str - The string to be tested.
 * @returns {boolean} - Returns true if the string is a valid URL, otherwise false.
 */
export const isUrl = (str: string): boolean => {
	try {
		return !!new URL(str);
	} catch {
		return false;
	}
};

export function formatAddress(address?: any): string {
	if (!address || typeof address !== 'object') return '';

	const street =
		typeof address.street === 'string' ? address.street.trim() : undefined;
	const houseNumber =
		address.houseNumber != null ? String(address.houseNumber) : undefined;
	const apartmentNumber =
		address.apartmentNumber != null
			? String(address.apartmentNumber)
			: undefined;
	const postalCode =
		typeof address.postalCode === 'string'
			? address.postalCode.trim()
			: undefined;
	const city =
		typeof address.city === 'string' ? address.city.trim() : undefined;
	const label =
		typeof address.label === 'string' ? address.label.trim() : undefined;

	if (street && houseNumber && postalCode && city) {
		const aptPart = apartmentNumber ? `/${apartmentNumber}` : '';
		return `${street} ${houseNumber}${aptPart}, ${postalCode} ${city}`;
	}

	if (street && postalCode && city) {
		return `${street}, ${postalCode} ${city}`;
	}

	if (postalCode && city) {
		return `${postalCode} ${city}`;
	}

	if (label) {
		return label;
	}

	const origCity = address.city ? address.city : '';
	const origStreet = address.street ? address.street : '';
	const origHouse = address.houseNumber ? ` ${address.houseNumber},` : '';
	const origPostal = address.postalCode ? address.postalCode : '';
	return `${origCity}, ${origStreet} str.,${origHouse} Zip ${origPostal}`;
}

export const addressWithoutCityCountry = (address?: any): string => {
	const formatted = formatAddress(address);
	const city = address?.city?.trim() ?? '';
	const country = address?.country?.trim() ?? '';

	if (city || country) {
		return formatted
			.replace(new RegExp(`,?\\s*${city}`, 'i'), '')
			.replace(new RegExp(`,?\\s*${country}`, 'i'), '')
			.replace(/,\s*,/g, ',')
			.replace(/^\s*,\s*/, '')
			.trim();
	}

	const parts = formatted.split(',').map((p) => p.trim());
	if (parts.length >= 3) {
		return parts
			.slice(0, parts.length - 2)
			.join(', ')
			.trim();
	}

	return formatted.trim();
};

export const cityCountryOnly = (address?: any): string => {
	if (address?.city || address?.country) {
		const parts: string[] = [];
		if (address.city) parts.push(address.city.trim());
		if (address.country) parts.push(address.country.trim());
		return parts.join(', ');
	}

	const formatted = formatAddress(address);
	const parts = formatted.split(',').map((p) => p.trim());
	if (parts.length >= 2) {
		const country = parts.pop()!;
		const cityWithPostal = parts.pop()!;
		const city = cityWithPostal.replace(/^\d{2}-\d{3}\s*/, '').trim();
		return [city, country].filter(Boolean).join(', ');
	}

	return '';
};

export const normalizeForSearch = (input: string) => {
	return input
		.normalize('NFD') // Decompose characters into base + combining marks
		.replace(/[\u0300-\u036f]/g, '') // Remove combining diacritical marks
		.replace(/[łŁ]/g, 'l') // Handle Polish ł specifically
		.replace(/[ąĄ]/g, 'a')
		.replace(/[ćĆ]/g, 'c')
		.replace(/[ęĘ]/g, 'e')
		.replace(/[ńŃ]/g, 'n')
		.replace(/[óÓ]/g, 'o')
		.replace(/[śŚ]/g, 's')
		.replace(/[źŹ]/g, 'z')
		.replace(/[żŻ]/g, 'z')
		.toLowerCase()
		.replace(/(.)\1+/g, '$1'); // Collapse any repeated letters
};

export const toSafeHref = (raw?: string): string | null => {
	const input = (raw ?? '').trim();
	if (!input) return null;

	const withScheme = /^[a-zA-Z][a-zA-Z\d+\-.]*:/.test(input)
		? input
		: `https://${input}`;

	try {
		const u = new URL(withScheme);
		const protoOk = u.protocol === 'http:' || u.protocol === 'https:';

		const hostOk = /^[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(u.hostname);
		return protoOk && hostOk ? u.href : null;
	} catch {
		return null;
	}
};
